import React from 'react';
import { 
  Box, 
  SimpleGrid, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText,
  Heading,
  Text,
  Flex,
  Progress,
  Skeleton,
  Badge,
  useColorModeValue
} from '@chakra-ui/react';
import { formatUSD, formatETH, formatPercentage, formatTokenAmount } from '../utils/formatters';
import { MAX_CONTRIBUTION, SOFT_CAP_ETH } from '../utils/constants';

const PresaleStats = ({ ethPrice, totalContributedEth, uniqueContributors, isLoading, tokenomics }) => {
  // Calculate some derived statistics
  const totalRaisedUsd = totalContributedEth * (ethPrice || 0);
  const maxContributionUsd = MAX_CONTRIBUTION * (ethPrice || 0);
  
  // Calculate progress percentage towards soft cap
  const progressPercentage = Math.min((totalContributedEth / SOFT_CAP_ETH) * 100, 100);
  
  // Calculate allocation per 0.1 ETH
  const tokensPerMaxContribution = tokenomics && tokenomics.metrics?.tokensPerEth
    ? tokenomics.metrics.tokensPerEth * MAX_CONTRIBUTION
    : 0;
  
  // Background styles
  const cardBg = useColorModeValue('rgba(26, 26, 26, 0.8)', 'rgba(26, 26, 26, 0.8)');
  const borderColor = useColorModeValue('yakuza.red', 'yakuza.red');
  
  return (
    <Box>
      <Heading as="h2" variant="title" mb={6}>
        Presale Overview
      </Heading>
      
      <Box 
        bg={cardBg}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        p={6}
        boxShadow="lg"
        position="relative"
        overflow="hidden"
      >
        {/* Background pattern element */}
        <Box 
          position="absolute"
          top="0"
          right="0"
          width="200px"
          height="200px"
          opacity="0.05"
          backgroundImage="url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cGF0aCBmaWxsPSIjYzQxZTNhIiBkPSJNNTAsMTAwIEwxMDAsNTAgTDUwLDAgTDAoNTApIi8+PC9zdmc+')"
          backgroundSize="cover"
          zIndex="0"
        />
        
        {/* Progress bar */}
        <Box mb={8} position="relative" zIndex="1">
          <Flex justify="space-between" mb={2} align="center">
            <Box>
              <Text fontWeight="bold" color="yakuza.white">Presale Progress</Text>
              <Text fontSize="sm" color="yakuza.gold">
                Soft Cap: {formatETH(SOFT_CAP_ETH)}
              </Text>
            </Box>
            <Flex align="center">
              <Text color="yakuza.gold" mr={2}>{formatETH(totalContributedEth)}</Text>
              {totalContributedEth >= SOFT_CAP_ETH && (
                <Badge colorScheme="green" variant="solid">
                  Soft Cap Reached
                </Badge>
              )}
            </Flex>
          </Flex>
          <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
            <Progress 
              value={progressPercentage} 
              colorScheme="red" 
              bg="yakuza.gray"
              borderRadius="full"
              height="12px"
            />
          </Skeleton>
          <Flex justify="flex-end" mt={1}>
            <Text fontSize="sm" color="yakuza.white">
              {formatPercentage(progressPercentage / 100)}
            </Text>
          </Flex>
        </Box>
        
        {/* Stats grid */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6} position="relative" zIndex="1">
          <Stat>
            <StatLabel color="yakuza.gold">Total Raised</StatLabel>
            <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
              <StatNumber color="yakuza.white">{formatETH(totalContributedEth)}</StatNumber>
              <StatHelpText color="yakuza.white">{formatUSD(totalRaisedUsd)}</StatHelpText>
            </Skeleton>
          </Stat>
          
          <Stat>
            <StatLabel color="yakuza.gold">ETH Price</StatLabel>
            <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
              <StatNumber color="yakuza.white">{formatUSD(ethPrice)}</StatNumber>
              <StatHelpText color="yakuza.white">Updated every minute</StatHelpText>
            </Skeleton>
          </Stat>
          
          <Stat>
            <StatLabel color="yakuza.gold">Allocation per {MAX_CONTRIBUTION} ETH</StatLabel>
            <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
              <StatNumber color="yakuza.white">
                {formatTokenAmount(tokensPerMaxContribution)}
              </StatNumber>
              <StatHelpText color="yakuza.white">YAKUZA tokens</StatHelpText>
            </Skeleton>
          </Stat>
          
          <Stat>
            <StatLabel color="yakuza.gold">Max Contribution</StatLabel>
            <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
              <StatNumber color="yakuza.white">{formatETH(MAX_CONTRIBUTION)}</StatNumber>
              <StatHelpText color="yakuza.white">{formatUSD(maxContributionUsd)}</StatHelpText>
            </Skeleton>
          </Stat>
        </SimpleGrid>
        
        {/* Instructions text */}
        <Box mt={8} p={4} bg="rgba(0,0,0,0.2)" borderRadius="md" position="relative" zIndex="1">
          <Heading as="h4" size="sm" color="yakuza.white" mb={2}>
            How to Participate
          </Heading>
          <Text color="yakuza.white" fontSize="sm">
            Send ETH directly to our presale wallet address on the Base network. 
            Minimum contribution: 0.001 ETH. Maximum contribution: 0.1 ETH. 
            Your YAKUZA tokens will be allocated based on your contribution amount.
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default PresaleStats;